import type { SetsKey } from './keys';

export const en: Record<SetsKey, string> = {
  'app.title': 'Sets',
  'app.eyebrow': 'Math · Sets',
  'app.tagline': 'Interactive set-theory experiments across the curriculum',
  'app.panel.heading': 'Parameters',
  'app.experiment-selector': 'Experiment',
  'btn.advanced': 'Advanced',
  'btn.reset': 'Reset',
  'btn.randomise': 'Randomise',

  // Action bar buttons (used by the harness)
  'action.reset': 'Reset',
  'action.randomise': 'Randomise',

  // ── set-builder-lab ──────────────────────────────────────────────────────
  'experiment.set-builder-lab.label': 'Set Builder Lab',
  'experiment.set-builder-lab.info':
    'Choose predicates for sets A and B, pick an operation, and watch the result update in the membership table and Euler diagram.',

  // Named predicate labels
  'set.even': 'Even',
  'set.odd': 'Odd',
  'set.prime': 'Prime',
  'set.multipleOf3': 'Multiple of 3',
  'set.multipleOf5': 'Multiple of 5',
  'set.gt10': 'Greater than 10',
  'set.lt5': 'Less than 5',

  // Panel parameter labels
  'param.predicateA': 'Set A',
  'param.predicateB': 'Set B',
  'param.predicateC': 'Set C',
  'param.op': 'Operation',

  // Operation labels
  'op.union': 'Union (∪)',
  'op.intersect': 'Intersection (∩)',
  'op.difference': 'Difference (∖)',
  'op.symDiff': 'Symmetric Difference (△)',
  'op.complement': 'Complement (∁)',

  // Stat labels
  'stat.result': 'Result',
  'stat.cardinality': '|Result|',
  'stat.inclusionExclusion': 'Inclusion–Exclusion',

  // Preset labels
  'preset.evenOdd': 'Even ∪ Odd',
  'preset.primeMult3': 'Prime ∩ Mult 3',

  // ── power-set ────────────────────────────────────────────────────────────
  'experiment.power-set.label': 'Power Set',
  'experiment.power-set.info':
    'Explore the power set 𝒫(S): every subset of S arranged in a Hasse diagram. ' +
    'Click a node to highlight a subset. Use the advanced cube layout to see the n-cube structure.',

  // Panel parameter labels
  'param.set-size': 'Set size (n)',
  'param.cube-layout': 'Cube layout',

  // Stat labels
  'stat.powerset-size': '|𝒫(S)|',

  // ── cartesian-product ────────────────────────────────────────────────────
  'experiment.cartesian-product.label': 'Cartesian Product',
  'experiment.cartesian-product.info':
    'Explore A×B as a grid of ordered pairs. Click a cell to highlight it. ' +
    'In advanced mode unlock A² (diagonal pairs) and A×B×C (triples).',

  // Panel parameter labels
  'param.size-a': '|A|',
  'param.size-b': '|B|',
  'param.mode': 'Mode',

  // Mode labels
  'mode.AxB': 'A×B',
  'mode.A2': 'A² (A×A)',
  'mode.AxBxC': 'A×B×C',

  // Stat labels
  'stat.product-size': '|A×B|',
  'stat.product-size-a2': '|A²|',
  'stat.product-size-axbxc': '|A×B×C|',

  // ── relations ────────────────────────────────────────────────────────────
  'experiment.relations.label': 'Relations',
  'experiment.relations.info':
    'Build a relation R ⊆ A×B by clicking the arrow diagram. ' +
    'In advanced mode set B := A and watch live reflexive / symmetric / antisymmetric / transitive badges update as you toggle pairs.',

  // Panel parameter labels
  'param.on-one-set': 'Relation on one set',
  'param.on-one-set.no': 'A×B (two sets)',
  'param.on-one-set.yes': 'A×A (one set)',

  // Relation property badges
  'prop.reflexive': 'Reflexive',
  'prop.symmetric': 'Symmetric',
  'prop.antisymmetric': 'Antisymmetric',
  'prop.transitive': 'Transitive',

  // Auto-name labels
  'name.equivalence': 'Equivalence relation',
  'name.partial-order': 'Partial order',

  // Stat labels
  'stat.relation-size': 'Relation size',

  // Preset labels (shared)
  'preset.empty': 'Empty (∅)',
  'preset.all': 'Full (A×B)',
  'preset.diagonal': 'Diagonal (identity)',

  // ── equivalence-partition ─────────────────────────────────────────────────
  'experiment.equivalence-partition.label': 'Equivalence & Partitions',
  'experiment.equivalence-partition.info':
    'Build a partition of the set by clicking elements to move them between blocks. ' +
    'In advanced mode toggle pairs to define an equivalence relation directly and see whether it induces a valid partition.',

  // Panel parameter labels
  'param.ep-mode': 'Mode',

  // Mode labels
  'mode.build-partition': 'Build partition',
  'mode.toggle-relation': 'Toggle relation',

  // Stat labels
  'stat.block-count': 'Blocks',
  'stat.bell-number': 'Bell number',
  'stat.quotient': 'Quotient set',

  // Error / validation messages
  'message.not-reflexive': 'Not an equivalence — fails reflexivity',
  'message.not-symmetric': 'Not an equivalence — fails symmetry',
  'message.not-transitive': 'Not an equivalence — fails transitivity',

  // Preset labels
  'preset.two-blocks': 'Two blocks',
  'preset.singletons': 'All singletons',
  'preset.one-block': 'One block',

  // ── functions ─────────────────────────────────────────────────────────────
  'experiment.functions.label': 'Functions',
  'experiment.functions.info':
    'Build a function f: A → B by clicking the arrow diagram. ' +
    'Each input gets exactly one output. In advanced mode see injective / surjective / bijective badges, ' +
    'collision and unhit highlights, and an inverse-exists indicator.',

  // Function property badges
  'prop.injective': 'Injective',
  'prop.surjective': 'Surjective',
  'prop.bijective': 'Bijective',

  // Stat labels
  'stat.domain-size': 'Domain',
  'stat.image-size': 'Image',

  // Label for compose / inverse readouts
  'label.compose': 'Compose',
  'label.inverse-exists': 'Inverse',

  // Column header labels
  'label.domain': 'domain',
  'label.codomain': 'codomain',

  // Preset labels
  'preset.fn-identity': 'Identity (bijection)',
  'preset.fn-injection': 'Injection (not surjective)',
  'preset.fn-surjection': 'Surjection (not injective)',

  // ── number-systems ────────────────────────────────────────────────────────
  'experiment.number-systems.label': 'Number Systems',
  'experiment.number-systems.info':
    'Explore the nesting ℕ⊂ℤ⊂ℚ⊂ℝ⊂ℂ. Select a number from the palette to see which sets it belongs to. ' +
    'In advanced mode enter any number and see it plotted on the complex plane.',

  // Why-not explanations (keys returned by whyNotIn)
  'message.why.N.negative': 'Negative — not in ℕ (which requires ≥ 0)',
  'message.why.N.not-integer': 'Not an integer — only integers can be in ℕ',
  'message.why.Z.not-integer': 'Not an integer — only integers are in ℤ',
  'message.why.Q.irrational': 'Irrational — cannot be expressed as a fraction p/q',
  'message.why.Q.complex': 'Complex — ℚ only contains real numbers',
  'message.why.R.complex': 'Complex — ℝ only contains real numbers (im = 0)',

  // Captions
  'caption.member-of': 'Member of',
  'caption.not-member-of': 'Not a member of',

  // Q-density note
  'note.q-density':
    'ℚ is dense in ℝ: between any two reals lies a rational — yet irrationals like √2 and π fill the gaps.',

  // Extension chain label
  'label.extension-chain': 'Extension chain',

  // Free-text input label
  'param.free-input': 'Enter a number',
};
